import { create } from 'zustand'
import { API_ENDPOINTS, API_BASE_URL } from '../config/api'
import type {
  TextFile,
  TriggerConfig,
  PrimaryChunk,
  SecondaryChunk,
  PreprocessingResult,
  BatchPreprocessingJob,
  BatchSegmentationJob,
  LLMConfig,
  MicroUnit,
  ChunkProcessingResult,
  CompleteMicroSegmentationResult,
  ProcessingSession,
  SavedSession,
  GroundTruthFile,
  GroundTruthInfo,
  WorkflowStep,
  WorkflowMode,
  APIResponse,
  WebSocketMessage
  } from '../types/segmentationTypes'
import type { WitnessResumeSession, BatchResumeSession } from '../types/resumeTypes'
import { useGroundTruthStore } from './groundTruthStore'
import { dbStorage } from '../lib/indexedDBStorage'

// Re-export types for backward compatibility
export type {
  TextFile,
  TriggerConfig,
  PrimaryChunk,
  SecondaryChunk,
  PreprocessingResult,
  BatchPreprocessingJob,
  BatchSegmentationJob,
  LLMConfig,
  MicroUnit,
  ChunkProcessingResult,
  CompleteMicroSegmentationResult,
  ProcessingSession,
  SavedSession,
  GroundTruthFile,
  GroundTruthInfo,
  WorkflowStep,
  WorkflowMode,
  APIResponse,
  WebSocketMessage
}


// ============================================================================
// Ground Truth Interface
// ============================================================================

interface GroundTruthData {
  witnessId: string
  witnessName: string
  segmentationResult: CompleteMicroSegmentationResult
  setTimestamp: string
  totalUnits: number
  totalChunks: number
}

// ============================================================================
// Store State Interface
// ============================================================================

interface SegmentationState {
  // Debug store tracking
  _storeInstanceId: string
  
  // Current workflow state
  currentStep: 'upload' | 'preprocessing' | 'chunk-selection' | 'processing' | 'results' | 'sessions' | 'batch-upload' | 'batch-preprocessing' | 'batch-chunk-selection' | 'batch-processing' | 'batch-results' | 'simplified-upload' | 'books-dashboard'
  workflowMode: WorkflowMode
  
  // Single file workflow
  uploadedFile: TextFile | null
  triggerConfig: TriggerConfig
  preprocessingResult: PreprocessingResult | null
  preprocessingLoading: boolean
  preprocessingError: string | null
  selectedChunks: string[]
  
  // Batch file workflow
  batchFiles: TextFile[]
  batchTriggerConfig: TriggerConfig
  batchPreprocessingJob: BatchPreprocessingJob | null
  batchSegmentationJob: BatchSegmentationJob | null
  batchSelectedChunks: Record<string, string[]> // file_id -> chunk_ids
  interruptionListeners: (() => void)[]
  
  // Resume functionality state
  currentResumeSession: WitnessResumeSession | null
  
  
  sidebarOpen: boolean
  selectedChunkForInspection: string | null
  
  // Force re-render mechanism for batch processing
  batchRenderCounter: number
  forceRenderCounter: number // EXPERT EMERGENCY ALTERNATIVE
  
  // Ground truth management
  groundTruthWitness: GroundTruthData | null
  
  // WebSocket connection for edit sessions
  setCurrentStep: (step: SegmentationState['currentStep']) => void
  setWorkflowMode: (mode: WorkflowMode) => void
  
  // Single file actions
  setUploadedFile: (file: TextFile | null) => void
  updateTriggerConfig: (config: Partial<TriggerConfig>) => void
  setPreprocessingResult: (result: PreprocessingResult | null) => void
  setUploadedFileForSync: (file: TextFile | null) => void
  setPreprocessingResultForSync: (result: PreprocessingResult | null) => void
  setPreprocessingLoading: (loading: boolean) => void
  setPreprocessingError: (error: string | null) => void
  setSelectedChunks: (chunks: string[]) => void
  toggleChunkSelection: (chunkId: string) => void
  
  // Batch file actions
  setBatchFiles: (files: TextFile[]) => void
  addBatchFile: (file: TextFile) => void
  removeBatchFile: (fileId: string) => void
  updateBatchTriggerConfig: (config: Partial<TriggerConfig>) => void
  setBatchPreprocessingJob: (job: BatchPreprocessingJob | null) => void
  updateBatchPreprocessingProgress: (updates: {
    current_file?: string
    completed_files?: number
    failed_files?: number
    errors?: string[]
  }) => void
  setBatchSegmentationJob: (job: BatchSegmentationJob | null) => void
  updateBatchSegmentationProgress: (updates: {
    completed_chunks?: number
    failed_chunks?: number
    current_file?: string
    current_chunk?: string
    results?: Record<string, any[]>
  }) => void
  setBatchSelectedChunks: (fileId: string, chunks: string[]) => void
  toggleBatchChunkSelection: (fileId: string, chunkId: string) => void
  
  // Processing state
  llmConfig: LLMConfig
  processingSession: ProcessingSession | null
  
  // Witness-scoped chunk results: Map<witnessId, Map<segmentationRunId, Map<chunkId, result>>>
  witnessChunkResults: Map<string, Map<string, Map<string, ChunkProcessingResult>>>
  
  // Current active witness and segmentation run for UI compatibility
  activeWitnessId: string | null
  activeSegmentationRunId: string | null
  
  // Emergency recovery state
  isRecovering: boolean
  recoveryComplete: boolean
  
  // Legacy single-file chunk results (for backward compatibility)
  chunkResults: Map<string, ChunkProcessingResult>
  
  updateLLMConfig: (config: Partial<LLMConfig>) => void
  setProcessingSession: (session: ProcessingSession | null) => void
  refreshProcessingResults: () => Promise<void>
  
  // Witness-scoped chunk result management
  setActiveWitness: (witnessId: string | null, segmentationRunId?: string) => void
  createSegmentationRun: (witnessId: string, runId?: string) => string
  updateWitnessChunkResult: (witnessId: string, runId: string, chunkId: string, result: ChunkProcessingResult) => void
  getWitnessChunkResults: (witnessId: string, runId?: string) => Map<string, ChunkProcessingResult>
  getWitnessSegmentationRuns: (witnessId: string) => string[]
  deleteSegmentationRun: (witnessId: string, runId: string) => void
  clearWitnessResults: (witnessId: string) => void
  
  // Segmentation run management and export
  exportWitnessRuns: (witnessId: string) => { [runId: string]: any }
  importWitnessRuns: (witnessId: string, runs: { [runId: string]: any }) => void
  duplicateSegmentationRun: (witnessId: string, sourceRunId: string, newRunId?: string) => string
  renameSegmentationRun: (witnessId: string, runId: string, newRunId: string) => boolean
  
  // Emergency recovery
  initializeEmergencyRecovery: () => Promise<void>
  
  // Legacy functions (for backward compatibility)
  updateChunkResult: (chunkId: string, result: ChunkProcessingResult) => void
  clearChunkResults: () => void
  
  setSidebarOpen: (open: boolean) => void
  setSelectedChunkForInspection: (chunkId: string | null) => void
  
  
  
  
  
  // Workflow actions
  startPreprocessing: () => void
  startBatchPreprocessing: () => void
  startProcessing: () => void
  startBatchProcessing: () => void
  pauseProcessing: () => void
  resumeProcessing: () => void
  stopProcessing: () => void
  
  // Batch processing control functions
  pauseBatchProcessing: () => Promise<void>
  resumeBatchProcessing: () => Promise<void>
  stopBatchProcessing: () => Promise<void>
  
  resetWorkflow: () => void
  
  // Witness-specific preprocessing actions
  startWitnessPreprocessing: (witnessFile: TextFile, config: TriggerConfig) => Promise<PreprocessingResult>
  preprocessWitnessWithConfig: (witnessId: string, witnessContent: string, config: TriggerConfig) => Promise<PreprocessingResult>
  createResumeSessionFromCurrentState: (witnessId: string, chapterId: string, bookId: string, interruption_reason: string) => Promise<WitnessResumeSession>
  createBatchResumeSessionFromCurrentState: (batchId: string, chapterId: string, bookId: string, interruption_reason: string) => Promise<BatchResumeSession>
  setupInterruptionDetection: (sessionId: string, context: {
    selectedChunks: string[]
    llmConfig: LLMConfig
    preprocessingResult: PreprocessingResult
    uploadedFile: TextFile | null
  }) => () => void // Returns cleanup function
  removeInterruptionDetection: () => void
  addInterruptionListener: (listener: () => void) => void
  removeInterruptionListener: (listener: () => void) => void
  
  // Ground truth management functions
  setGroundTruthWitness: (groundTruth: GroundTruthData | null) => void
  clearGroundTruth: () => void
  autoApplyWithGroundTruth: (sessionId: string, witnessId?: string) => Promise<void>
  
  // Resume session management (basic interruption handling only)
  markResumeSessionComplete: () => Promise<void>
  handleProcessingInterruption: (reason: string) => Promise<void>
  
  // Helper function for localStorage persistence
  _persistChunkResults: (chunkResults: Map<string, ChunkProcessingResult>) => void
  _persistGroundTruthWitness: (groundTruth: GroundTruthData | null) => void
  
  // Note: Unit editing functionality available in unitEditStore.ts
  
  // Note: Session management available in sessionStore.ts
  
  // Note: Unit editing actions available in unitEditStore.ts
  
  // Note: Resume functionality available in resumeStore.ts
}

// ============================================================================
// Default Configuration Values
// ============================================================================

const defaultTriggerConfig: TriggerConfig = {
  primary_trigger: 'قال الملك',
  secondary_trigger: 'قال',
  min_primary_chunk_size: 50,  // Reference value only - all chunks extracted regardless
  min_secondary_chunk_size: 25, // Reference value only - all chunks extracted regardless
  include_primary_trigger: true,
  include_secondary_trigger: true,
  excluded_words: ['ذكر', 'روى', 'حدث'],
  excluded_patterns: ['\\d+']
}

const defaultLLMConfig: LLMConfig = {
  provider: 'openai',
  model: 'gpt-4o',
  max_chunk_snippet: 1024,
  enable_model_escalation: false,
  method: 'semantic_first',
  confidence_threshold: 0.8,
  ground_truth_mode: false,
  debug_matching: false
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useSegmentationStore = create<SegmentationState>((set, get) => {
  // Note: Session loading is now handled by sessionStore.ts

  // Load current chunkResults from localStorage if available
  let initialChunkResults: Map<string, any> = new Map()
  try {
    const savedChunkResults = localStorage.getItem('segmentation_current_chunk_results')
    if (savedChunkResults) {
      const parsed = JSON.parse(savedChunkResults)
      initialChunkResults = new Map(parsed || [])
      console.log('Restored chunkResults from localStorage:', initialChunkResults.size, 'chunks')
    }
  } catch (error) {
    console.error('Failed to load chunkResults from localStorage:', error)
  }

  // Load ground truth witness from storage if available
  let initialGroundTruthWitness: GroundTruthData | null = null
  try {
    // First try to load the reference from localStorage
    const savedReference = localStorage.getItem('segmentation_ground_truth_reference')
    console.log('🔍 STORE INIT: localStorage groundTruthReference:', savedReference ? 'found' : 'not found')
    
    if (savedReference) {
      const reference = JSON.parse(savedReference)
      console.log('🔍 STORE INIT: Ground truth reference loaded:', {
        witnessName: reference.witnessName,
        totalUnits: reference.totalUnits,
        sessionReference: reference.sessionReference
      })
      
      // Try to load the full data from sessionStorage
      const sessionKey = `segmentation_ground_truth_data_${reference.sessionReference}`
      const fullData = sessionStorage.getItem(sessionKey)
      
      if (fullData) {
        initialGroundTruthWitness = JSON.parse(fullData)
        console.log('🔍 STORE INIT: Full ground truth data restored from sessionStorage:', {
          witnessName: initialGroundTruthWitness?.witnessName,
          totalUnits: initialGroundTruthWitness?.totalUnits,
          hasSegmentationResult: !!initialGroundTruthWitness?.segmentationResult,
          dataSize: fullData.length + ' bytes'
        })
      } else {
        console.warn('🔍 STORE INIT: Ground truth reference found but full data missing from sessionStorage')
        // Could show a message to user that ground truth needs to be re-set
      }
    } else {
      // Fallback: Check for old format or minimal reference
      const oldFormat = localStorage.getItem('segmentation_ground_truth_witness')
      const minimalRef = localStorage.getItem('segmentation_ground_truth_minimal')
      
      if (oldFormat) {
        console.log('🔍 STORE INIT: Found old format ground truth, attempting migration...')
        localStorage.removeItem('segmentation_ground_truth_witness') // Clean up old format
      } else if (minimalRef) {
        console.log('🔍 STORE INIT: Found minimal ground truth reference:', minimalRef)
      } else {
        console.log('🔍 STORE INIT: No ground truth data found in any storage')
      }
    }
  } catch (error) {
    console.error('🔍 STORE INIT: Failed to load groundTruthWitness from storage:', error)
  }

  // Debug: Store instance tracking
  const storeInstanceId = Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  console.log('🔍 STORE CREATION: New store instance created:', storeInstanceId)

  return {
    // Store instance ID for debugging
    _storeInstanceId: storeInstanceId,
    
    // Initial state
    currentStep: 'upload',
    workflowMode: 'single',
  
  // Single file workflow
  uploadedFile: null,
  triggerConfig: defaultTriggerConfig,
  preprocessingResult: null,
  preprocessingLoading: false,
  preprocessingError: null,
  selectedChunks: [],
  
  // Batch workflow
  batchFiles: [],
  batchTriggerConfig: defaultTriggerConfig,
  batchPreprocessingJob: null,
  batchSegmentationJob: null,
  batchSelectedChunks: {},
  
  llmConfig: defaultLLMConfig,
  processingSession: null,
  
  // Witness-scoped chunk results
  witnessChunkResults: new Map(),
  activeWitnessId: null,
  activeSegmentationRunId: null,
  
  // Emergency recovery state
  isRecovering: false,
  recoveryComplete: false,
  
  // Ground truth management
  groundTruthWitness: initialGroundTruthWitness,
  
  // Legacy chunk results
  chunkResults: initialChunkResults,
  
  // Interruption detection
  interruptionListeners: [],
  
  // Resume state
  currentResumeSession: null,

  // Note: Unit editing, session management, and resume functionality
  // are now available in their respective dedicated stores

  // Helper function to persist chunkResults to localStorage
  _persistChunkResults: (chunkResults: Map<string, any>) => {
    try {
      const serialized = Array.from(chunkResults.entries())
      localStorage.setItem('segmentation_current_chunk_results', JSON.stringify(serialized))
      console.log('Persisted chunkResults to localStorage:', chunkResults.size, 'chunks')
    } catch (error) {
      console.error('Failed to persist chunkResults to localStorage:', error)
    }
  },

  // Helper function for ground truth localStorage persistence
  _persistGroundTruthWitness: (groundTruth: GroundTruthData | null) => {
    try {
      if (groundTruth) {
        // Store only essential reference data, not the entire segmentation result
        const groundTruthReference = {
          witnessId: groundTruth.witnessId,
          witnessName: groundTruth.witnessName,
          setTimestamp: groundTruth.setTimestamp,
          totalUnits: groundTruth.totalUnits,
          totalChunks: groundTruth.totalChunks,
          // Store a reference to retrieve the full data from session storage or API
          sessionReference: `gt_${groundTruth.witnessId}_${groundTruth.setTimestamp}`
        }
        
        // Store the reference in localStorage (small)
        localStorage.setItem('segmentation_ground_truth_reference', JSON.stringify(groundTruthReference))
        
        // Store the full data in sessionStorage (larger capacity, cleared on tab close)
        const sessionKey = `segmentation_ground_truth_data_${groundTruthReference.sessionReference}`
        sessionStorage.setItem(sessionKey, JSON.stringify(groundTruth))
        
        console.log('Persisted groundTruthWitness:', {
          reference: 'localStorage (reference only)',
          fullData: 'sessionStorage (full data)',
          witnessName: groundTruth.witnessName,
          dataSize: JSON.stringify(groundTruth).length + ' bytes'
        })
      } else {
        // Clear both localStorage and sessionStorage
        localStorage.removeItem('segmentation_ground_truth_reference')
        
        // Clear all ground truth data from sessionStorage
        const keysToRemove = []
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i)
          if (key && key.startsWith('segmentation_ground_truth_data_')) {
            keysToRemove.push(key)
          }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key))
        
        console.log('Removed groundTruthWitness from storage')
      }
    } catch (error) {
      console.error('Failed to persist groundTruthWitness:', error)
      
      // Fallback: Try to at least save the reference
      if (groundTruth && error instanceof Error && error.name === 'QuotaExceededError') {
        try {
          const minimalReference = {
            witnessName: groundTruth.witnessName,
            totalUnits: groundTruth.totalUnits,
            setTimestamp: groundTruth.setTimestamp,
            error: 'Full data too large for storage'
          }
          localStorage.setItem('segmentation_ground_truth_minimal', JSON.stringify(minimalReference))
          console.warn('Saved minimal ground truth reference due to quota exceeded')
        } catch (e) {
          console.error('Even minimal reference failed to save:', e)
        }
      }
    }
  },
  
  
  sidebarOpen: false,
  selectedChunkForInspection: null,
  
  // Force re-render mechanism
  batchRenderCounter: 0,
  forceRenderCounter: 0, // EXPERT EMERGENCY ALTERNATIVE
  
  // WebSocket management
  // WebSocket connection for edit sessions
  setCurrentStep: (step) => set({ currentStep: step }),
  setWorkflowMode: (mode) => {
    // Clear previous results when switching workflow modes
    const { clearChunkResults } = get()
    clearChunkResults()
    set({ workflowMode: mode })
  },
  
  // Single file actions
  setUploadedFile: (file) => {
    // Clear previous results when uploading a new file (but not when syncing witness data)
    const { clearChunkResults } = get()
    
    // Don't clear chunk results if we're setting the same file (sync operation)
    const currentFile = get().uploadedFile
    const isSyncOperation = currentFile && file && currentFile.id === file.id
    
    if (!isSyncOperation) {
      clearChunkResults()
    }
    
    set({ uploadedFile: file })
  },
  updateTriggerConfig: (config) => set((state) => ({
    triggerConfig: { ...state.triggerConfig, ...config }
  })),
  setPreprocessingResult: (result) => set({ preprocessingResult: result }),
  
  // Sync-safe versions that don't clear chunk results (for witness sync operations)
  setUploadedFileForSync: (file) => set({ uploadedFile: file }),
  setPreprocessingResultForSync: (result) => set({ preprocessingResult: result }),
  setPreprocessingLoading: (loading) => set({ preprocessingLoading: loading }),
  setPreprocessingError: (error) => set({ preprocessingError: error }),
  setSelectedChunks: (chunks) => set({ selectedChunks: chunks }),
  toggleChunkSelection: (chunkId) => set((state) => ({
    selectedChunks: state.selectedChunks.includes(chunkId)
      ? state.selectedChunks.filter(id => id !== chunkId)
      : [...state.selectedChunks, chunkId]
  })),
  
  // Batch file actions
  setBatchFiles: (files) => {
    // Clear previous results when setting new batch files
    const { clearChunkResults } = get()
    clearChunkResults()
    set({ batchFiles: files })
  },
  addBatchFile: (file) => {
    // Clear previous results when adding first batch file
    const { batchFiles, clearChunkResults } = get()
    if (batchFiles.length === 0) {
      clearChunkResults()
    }
    set((state) => ({
      batchFiles: [...state.batchFiles, file]
    }))
  },
  removeBatchFile: (fileId) => set((state) => ({
    batchFiles: state.batchFiles.filter(f => f.id !== fileId)
  })),
  updateBatchTriggerConfig: (config) => set((state) => ({
    batchTriggerConfig: { ...state.batchTriggerConfig, ...config }
  })),
  setBatchPreprocessingJob: (job) => set({ batchPreprocessingJob: job }),
  updateBatchPreprocessingProgress: (updates) => set((state) => {
    if (!state.batchPreprocessingJob) return state
    
    // Atomic batch preprocessing progress update
    const updatedJob = {
      ...state.batchPreprocessingJob,
      progress: {
        ...state.batchPreprocessingJob.progress,
        ...updates
      }
    }
    
    return {
      batchPreprocessingJob: updatedJob,
      // Increment render counter atomically for UI updates
      batchRenderCounter: state.batchRenderCounter + 1
    }
  }),
  setBatchSegmentationJob: (job) => set((state) => ({
    batchSegmentationJob: job,
    // Increment render counter for UI updates (atomic operation)
    batchRenderCounter: state.batchRenderCounter + 1
  })),
  
  // NEW: Atomic batch segmentation progress update to prevent race conditions
  updateBatchSegmentationProgress: (updates: {
    completed_chunks?: number
    failed_chunks?: number
    current_file?: string
    current_chunk?: string
    results?: Record<string, any[]>
  }) => set((state) => {
    if (!state.batchSegmentationJob) return state
    
    // Create atomic update
    const updatedJob = {
      ...state.batchSegmentationJob,
      progress: {
        ...state.batchSegmentationJob.progress,
        ...updates
      },
      // Update results if provided
      ...(updates.results && { results: updates.results })
    }
    
    return {
      batchSegmentationJob: updatedJob,
      batchRenderCounter: state.batchRenderCounter + 1
    }
  }),
  setBatchSelectedChunks: (fileId, chunks) => set((state) => {
    return { 
      batchSelectedChunks: {
        ...state.batchSelectedChunks,
        [fileId]: chunks
      }
    }
  }),
  toggleBatchChunkSelection: (fileId, chunkId) => set((state) => {
    const currentChunks = state.batchSelectedChunks[fileId] || []
    const newChunks = currentChunks.includes(chunkId)
      ? currentChunks.filter(id => id !== chunkId)
      : [...currentChunks, chunkId]
    return { 
      batchSelectedChunks: {
        ...state.batchSelectedChunks,
        [fileId]: newChunks
      }
    }
  }),
  
  updateLLMConfig: (config) => set((state) => ({
    llmConfig: { ...state.llmConfig, ...config }
  })),
  setProcessingSession: (session) => set({ processingSession: session }),
  // Witness-scoped chunk result management
  setActiveWitness: (witnessId, segmentationRunId) => {
    const state = get()
    console.log('[SetActiveWitness] Setting active witness:', { witnessId, segmentationRunId })
    
    set({ 
      activeWitnessId: witnessId,
      activeSegmentationRunId: segmentationRunId || null
    })
    
    // Update legacy chunkResults for UI compatibility
    if (witnessId && segmentationRunId) {
      const witnessResults = state.witnessChunkResults.get(witnessId)
      const runResults = witnessResults?.get(segmentationRunId)
      if (runResults) {
        set({ chunkResults: new Map(runResults) })
        console.log('[SetActiveWitness] Loaded chunk results for UI:', runResults.size, 'chunks')
      } else {
        // EMERGENCY RECOVERY: Try to load from IndexedDB if memory is empty
        console.log('[SetActiveWitness] Memory empty, attempting emergency recovery from IndexedDB...')
        dbStorage.getWitnessChunkResults(witnessId, segmentationRunId)
          .then(recoveredResults => {
            if (recoveredResults.size > 0) {
              console.log('🔄 Emergency recovery successful:', { witnessId, segmentationRunId, recovered: recoveredResults.size })
              
              // Update memory with recovered data
              set((currentState) => {
                const newWitnessResults = new Map(currentState.witnessChunkResults)
                
                if (!newWitnessResults.has(witnessId)) {
                  newWitnessResults.set(witnessId, new Map())
                }
                
                const witnessRuns = newWitnessResults.get(witnessId)!
                witnessRuns.set(segmentationRunId, recoveredResults)
                
                return { 
                  witnessChunkResults: newWitnessResults,
                  chunkResults: new Map(recoveredResults)
                }
              })
            } else {
              set({ chunkResults: new Map() })
            }
          })
          .catch(error => {
            console.error('❌ Emergency recovery failed:', { witnessId, segmentationRunId, error })
            set({ chunkResults: new Map() })
          })
      }
    } else {
      set({ chunkResults: new Map() })
    }
  },
  
  createSegmentationRun: (witnessId, runId) => {
    const generatedRunId = runId || `run_${Date.now()}`
    console.log('[CreateSegmentationRun] Creating run:', { witnessId, runId: generatedRunId })
    
    set((state) => {
      const newWitnessResults = new Map(state.witnessChunkResults)
      
      if (!newWitnessResults.has(witnessId)) {
        newWitnessResults.set(witnessId, new Map())
      }
      
      const witnessRuns = newWitnessResults.get(witnessId)!
      witnessRuns.set(generatedRunId, new Map())
      
      return { 
        witnessChunkResults: newWitnessResults,
        activeWitnessId: witnessId,
        activeSegmentationRunId: generatedRunId
      }
    })
    
    return generatedRunId
  },
  
  updateWitnessChunkResult: (witnessId, runId, chunkId, result) => {
    console.log('[UpdateWitnessChunkResult] Called:', { witnessId, runId, chunkId })
    
    set((state) => {
      const newWitnessResults = new Map(state.witnessChunkResults)
      
      // Ensure witness exists
      if (!newWitnessResults.has(witnessId)) {
        newWitnessResults.set(witnessId, new Map())
      }
      
      const witnessRuns = newWitnessResults.get(witnessId)!
      
      // Ensure run exists
      if (!witnessRuns.has(runId)) {
        witnessRuns.set(runId, new Map())
      }
      
      const runResults = witnessRuns.get(runId)!
      runResults.set(chunkId, result)
      
      // Update legacy chunkResults if this is the active witness/run
      let updatedChunkResults = state.chunkResults
      if (state.activeWitnessId === witnessId && state.activeSegmentationRunId === runId) {
        updatedChunkResults = new Map(runResults)
      }
      
      console.log('[UpdateWitnessChunkResult] Updated witness results:', {
        witnessId,
        runId,
        chunkCount: runResults.size,
        isActive: state.activeWitnessId === witnessId && state.activeSegmentationRunId === runId
      })
      
      return { 
        witnessChunkResults: newWitnessResults,
        chunkResults: updatedChunkResults
      }
    })
    
    // EMERGENCY PERSISTENCE: Save to IndexedDB immediately to prevent data loss
    dbStorage.saveWitnessChunkResult(witnessId, runId, chunkId, result)
      .then(() => {
        console.log('💾 Emergency persistence successful:', { witnessId, runId, chunkId })
      })
      .catch(error => {
        console.error('❌ Emergency persistence failed:', { witnessId, runId, chunkId, error })
        // Don't throw error to avoid breaking the UI, but log for debugging
      })
  },
  
  getWitnessChunkResults: (witnessId, runId) => {
    const state = get()
    const witnessResults = state.witnessChunkResults.get(witnessId)
    if (!witnessResults) {
      console.log('[GetWitnessChunkResults] No results for witness:', witnessId)
      return new Map()
    }
    
    // If no runId specified, use the active one or the latest one
    const targetRunId = runId || state.activeSegmentationRunId || Array.from(witnessResults.keys()).pop()
    if (!targetRunId) {
      console.log('[GetWitnessChunkResults] No run ID available for witness:', witnessId)
      return new Map()
    }
    
    const runResults = witnessResults.get(targetRunId) || new Map()
    console.log('[GetWitnessChunkResults] Retrieved results:', { witnessId, runId: targetRunId, chunkCount: runResults.size })
    return runResults
  },
  
  getWitnessSegmentationRuns: (witnessId) => {
    const state = get()
    const witnessResults = state.witnessChunkResults.get(witnessId)
    const runs = witnessResults ? Array.from(witnessResults.keys()) : []
    console.log('[GetWitnessSegmentationRuns] Found runs for witness:', { witnessId, runs })
    return runs
  },
  
  deleteSegmentationRun: (witnessId, runId) => {
    console.log('[DeleteSegmentationRun] Deleting run:', { witnessId, runId })
    
    set((state) => {
      const newWitnessResults = new Map(state.witnessChunkResults)
      const witnessRuns = newWitnessResults.get(witnessId)
      
      if (witnessRuns) {
        witnessRuns.delete(runId)
        
        // If this was the active run, clear active state
        const updatedState: any = { witnessChunkResults: newWitnessResults }
        
        if (state.activeWitnessId === witnessId && state.activeSegmentationRunId === runId) {
          updatedState.activeSegmentationRunId = null
          updatedState.chunkResults = new Map()
        }
        
        return updatedState
      }
      
      return { witnessChunkResults: newWitnessResults }
    })
  },
  
  clearWitnessResults: (witnessId) => {
    console.log('[ClearWitnessResults] Clearing all results for witness:', witnessId)
    
    set((state) => {
      const newWitnessResults = new Map(state.witnessChunkResults)
      newWitnessResults.delete(witnessId)
      
      // If this was the active witness, clear active state
      const updatedState: any = { witnessChunkResults: newWitnessResults }
      
      if (state.activeWitnessId === witnessId) {
        updatedState.activeWitnessId = null
        updatedState.activeSegmentationRunId = null
        updatedState.chunkResults = new Map()
      }
      
      return updatedState
    })
  },
  
  // Segmentation run management and export functions
  exportWitnessRuns: (witnessId) => {
    const state = get()
    const witnessResults = state.witnessChunkResults.get(witnessId)
    
    if (!witnessResults) {
      console.log('[ExportWitnessRuns] No runs found for witness:', witnessId)
      return {}
    }
    
    const exportData: { [runId: string]: any } = {}
    
    Array.from(witnessResults.entries()).forEach(([runId, chunkResults]) => {
      exportData[runId] = {
        metadata: {
          runId,
          witnessId,
          createdAt: new Date().toISOString(),
          chunkCount: chunkResults.size
        },
        chunks: Object.fromEntries(Array.from(chunkResults.entries()))
      }
    })
    
    console.log('[ExportWitnessRuns] Exported runs for witness:', { witnessId, runCount: Object.keys(exportData).length })
    return exportData
  },
  
  importWitnessRuns: (witnessId, runs) => {
    console.log('[ImportWitnessRuns] Importing runs for witness:', { witnessId, runCount: Object.keys(runs).length })
    
    set((state) => {
      const newWitnessResults = new Map(state.witnessChunkResults)
      
      if (!newWitnessResults.has(witnessId)) {
        newWitnessResults.set(witnessId, new Map())
      }
      
      const witnessRuns = newWitnessResults.get(witnessId)!
      
      Object.entries(runs).forEach(([runId, runData]) => {
        const chunkMap = new Map(Object.entries(runData.chunks)) as Map<string, ChunkProcessingResult>
        witnessRuns.set(runId, chunkMap)
        console.log('[ImportWitnessRuns] Imported run:', { runId, chunkCount: chunkMap.size })
      })
      
      return { witnessChunkResults: newWitnessResults }
    })
  },
  
  duplicateSegmentationRun: (witnessId, sourceRunId, newRunId) => {
    const state = get()
    const witnessResults = state.witnessChunkResults.get(witnessId)
    const sourceRun = witnessResults?.get(sourceRunId)
    
    if (!sourceRun) {
      console.error('[DuplicateSegmentationRun] Source run not found:', { witnessId, sourceRunId })
      return ''
    }
    
    const generatedNewRunId = newRunId || `${sourceRunId}_copy_${Date.now()}`
    
    set((currentState) => {
      const newWitnessResults = new Map(currentState.witnessChunkResults)
      const witnessRuns = newWitnessResults.get(witnessId)!
      
      // Deep copy the source run
      const duplicatedRun = new Map<string, ChunkProcessingResult>()
      Array.from(sourceRun.entries()).forEach(([chunkId, result]) => {
        duplicatedRun.set(chunkId, { ...result })
      })
      
      witnessRuns.set(generatedNewRunId, duplicatedRun)
      
      console.log('[DuplicateSegmentationRun] Duplicated run:', {
        witnessId,
        sourceRunId,
        newRunId: generatedNewRunId,
        chunkCount: duplicatedRun.size
      })
      
      return { witnessChunkResults: newWitnessResults }
    })
    
    return generatedNewRunId
  },
  
  renameSegmentationRun: (witnessId, runId, newRunId) => {
    const state = get()
    const witnessResults = state.witnessChunkResults.get(witnessId)
    const targetRun = witnessResults?.get(runId)
    
    if (!witnessResults || !targetRun) {
      console.error('[RenameSegmentationRun] Witness or run not found:', { witnessId, runId })
      return false
    }
    
    if (witnessResults.has(newRunId)) {
      console.error('[RenameSegmentationRun] New run ID already exists:', { witnessId, newRunId })
      return false
    }
    
    set((currentState) => {
      const newWitnessResults = new Map(currentState.witnessChunkResults)
      const witnessRuns = newWitnessResults.get(witnessId)!
      
      // Remove old entry and add with new key
      witnessRuns.delete(runId)
      witnessRuns.set(newRunId, targetRun)
      
      // Update active run ID if this was the active run
      const updatedState: any = { witnessChunkResults: newWitnessResults }
      
      if (currentState.activeWitnessId === witnessId && currentState.activeSegmentationRunId === runId) {
        updatedState.activeSegmentationRunId = newRunId
      }
      
      console.log('[RenameSegmentationRun] Renamed run:', { witnessId, oldRunId: runId, newRunId })
      
      return updatedState
    })
    
    return true
  },
  
  // EMERGENCY RECOVERY: Initialize recovery system
  initializeEmergencyRecovery: async () => {
    console.log('🔄 Initializing emergency recovery system...')
    set({ isRecovering: true, recoveryComplete: false })
    
    try {
      // Recovery happens automatically when setActiveWitness is called
      // This method just sets up the recovery state
      console.log('💾 Emergency recovery system initialized')
      set({ isRecovering: false, recoveryComplete: true })
    } catch (error) {
      console.error('❌ Emergency recovery system failed to initialize:', error)
      set({ isRecovering: false, recoveryComplete: false })
    }
  },
  
  // Legacy updateChunkResult (routes to witness-scoped version)
  updateChunkResult: (chunkId, result) => {
    const state = get()
    console.log('[UpdateChunkResult] Legacy call - routing to witness-scoped version:', { 
      chunkId, 
      activeWitnessId: state.activeWitnessId,
      activeSegmentationRunId: state.activeSegmentationRunId
    })
    
    // Route to witness-scoped version if active witness is set
    if (state.activeWitnessId && state.activeSegmentationRunId) {
      const { updateWitnessChunkResult } = get()
      updateWitnessChunkResult(state.activeWitnessId, state.activeSegmentationRunId, chunkId, result)
      return
    }
    
    // Fallback to legacy behavior for non-witness workflows
    set((legacyState) => {
      const entries = Array.from(legacyState.chunkResults.entries())
      const existingIndex = entries.findIndex(([id]) => id === chunkId)
      if (existingIndex !== -1) {
        entries.splice(existingIndex, 1)
      }
      entries.push([chunkId, result])
      
      entries.sort(([, a], [, b]) => {
        const aPos = a.micro_units?.[0]?.start_position ?? 0
        const bPos = b.micro_units?.[0]?.start_position ?? 0
        return aPos - bPos
      })
      
      const newResults = new Map(entries)
      const { _persistChunkResults } = get()
      _persistChunkResults(newResults)
      
      return { chunkResults: newResults }
    })
  },
  
  // Legacy clearChunkResults (clears only active witness results)
  clearChunkResults: () => {
    const state = get()
    console.log('[ClearChunkResults] Legacy call - clearing active witness results only')
    
    // If we have an active witness, clear only their active run
    if (state.activeWitnessId && state.activeSegmentationRunId) {
      const { deleteSegmentationRun } = get()
      deleteSegmentationRun(state.activeWitnessId, state.activeSegmentationRunId)
      return
    }
    
    // Fallback to legacy behavior
    set((legacyState) => {
      const clearedResults = new Map<string, ChunkProcessingResult>()
      const { _persistChunkResults } = get()
      _persistChunkResults(clearedResults)
      
      console.log('[ClearChunkResults] Cleared legacy chunk results and localStorage')
      return { 
        chunkResults: clearedResults,
        batchSegmentationJob: legacyState.batchSegmentationJob ? {
          ...legacyState.batchSegmentationJob,
          results: {}
        } : null
      }
    })
  },
  
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setSelectedChunkForInspection: (chunkId) => set({ selectedChunkForInspection: chunkId }),
  
  // Workflow actions
  startPreprocessing: async () => {
    const { uploadedFile, triggerConfig, clearChunkResults } = get()
    if (!uploadedFile) return
    
    // Clear previous results when starting new preprocessing
    clearChunkResults()
    
    // Validate trigger configuration
    if (!triggerConfig.primary_trigger || !triggerConfig.secondary_trigger) {
      set({ preprocessingError: 'Primary and secondary triggers must be specified' })
      return
    }
    
    set({ preprocessingLoading: true, preprocessingError: null })
    
    // Log trigger configuration for debugging
    console.log('🔍 Preprocessing with trigger config:', {
      primary_trigger: triggerConfig.primary_trigger,
      secondary_trigger: triggerConfig.secondary_trigger,
      min_primary_chunk_size: triggerConfig.min_primary_chunk_size,
      min_secondary_chunk_size: triggerConfig.min_secondary_chunk_size,
      include_primary_trigger: triggerConfig.include_primary_trigger,
      include_secondary_trigger: triggerConfig.include_secondary_trigger
    })
    
    try {
      // API call to preprocessing endpoint
      const response = await fetch(API_ENDPOINTS.preprocessing, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: uploadedFile.content,
          config: triggerConfig
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Preprocessing failed')
      }
      
      const result = await response.json()
      set({ 
        preprocessingResult: { ...result, file_id: uploadedFile.id },
        preprocessingLoading: false,
        currentStep: 'chunk-selection'
      })
    } catch (error) {
      set({ 
        preprocessingError: error instanceof Error ? error.message : 'Unknown error',
        preprocessingLoading: false
      })
    }
  },

  startBatchPreprocessing: async () => {
    const { batchFiles, batchTriggerConfig, clearChunkResults } = get()
    if (!batchFiles.length) return
    
    // Clear previous results when starting new batch preprocessing
    clearChunkResults()
    
    // Validate trigger configuration
    if (!batchTriggerConfig.primary_trigger || !batchTriggerConfig.secondary_trigger) {
      const jobId = `batch_${Date.now()}_error`
      set({ 
        batchPreprocessingJob: {
          job_id: jobId,
          files: batchFiles,
          trigger_config: batchTriggerConfig,
          status: 'error',
          progress: {
            total_files: batchFiles.length,
            completed_files: 0,
            failed_files: batchFiles.length
          },
          results: {},
          start_time: Date.now(),
          error_message: 'Primary and secondary triggers must be specified'
        }
      })
      return
    }
    
    console.log('🔍 Starting batch preprocessing for', batchFiles.length, 'files with config:', {
      primary_trigger: batchTriggerConfig.primary_trigger,
      secondary_trigger: batchTriggerConfig.secondary_trigger,
      min_primary_chunk_size: batchTriggerConfig.min_primary_chunk_size,
      min_secondary_chunk_size: batchTriggerConfig.min_secondary_chunk_size
    })
    
    const jobId = `batch_${Date.now()}`
    const job: BatchPreprocessingJob = {
      job_id: jobId,
      files: batchFiles,
      trigger_config: batchTriggerConfig,
      status: 'processing',
      progress: {
        total_files: batchFiles.length,
        completed_files: 0,
        failed_files: 0,
        current_file: batchFiles[0]?.name
      },
      results: {},
      start_time: Date.now()
    }
    
    set({ batchPreprocessingJob: job, currentStep: 'batch-preprocessing' })
    
    try {
      const results: Record<string, PreprocessingResult> = {}
      let completedFiles = 0
      let failedFiles = 0
      
      // Process files sequentially with real-time progress updates
      for (let i = 0; i < batchFiles.length; i++) {
        const file = batchFiles[i]
        
        // Update progress to show current file
        get().updateBatchPreprocessingProgress({
          current_file: file.name,
          completed_files: completedFiles
        })
        
        console.log(`🔍 Processing file ${i + 1}/${batchFiles.length}: ${file.name}`)
        
        try {
          // API call for individual file preprocessing
          const response = await fetch(API_ENDPOINTS.preprocessing, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              text: file.content,
              config: batchTriggerConfig
            })
          })
          
          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.detail || 'Preprocessing failed')
          }
          
          const result = await response.json()
          results[file.id] = { ...result, file_id: file.id }
          completedFiles++
          
          console.log(`✅ Successfully processed: ${file.name}`)
          
        } catch (error) {
          console.error(`❌ Failed to process ${file.name}:`, error)
          failedFiles++
          
          // Update progress to reflect failure
          const currentJob = get().batchPreprocessingJob
          get().updateBatchPreprocessingProgress({
            failed_files: failedFiles,
            errors: [
              ...(currentJob?.progress.errors || []),
              `${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`
            ]
          })
        }
        
        // Small delay to ensure UI updates are visible
        await new Promise(resolve => setTimeout(resolve, 100))
      }
      
      // Final update with completion status
      const finalJob: BatchPreprocessingJob = {
        ...job,
        status: 'completed',
        completion_time: Date.now(),
        progress: {
          total_files: batchFiles.length,
          completed_files: completedFiles,
          failed_files: failedFiles,
          current_file: undefined,
          errors: failedFiles > 0 ? [`${failedFiles} files failed to process`] : undefined
        },
        results
      }
      
      console.log('🎉 Batch preprocessing completed:', {
        total: batchFiles.length,
        completed: completedFiles,
        failed: failedFiles,
        results: Object.keys(results).length
      })
      
      set({ 
        batchPreprocessingJob: finalJob,
        currentStep: 'batch-chunk-selection'
      })
      
    } catch (error) {
      console.error('❌ Batch preprocessing failed:', error)
      const failedJob: BatchPreprocessingJob = {
        ...job,
        status: 'error',
        error_message: error instanceof Error ? error.message : 'Unknown error',
        completion_time: Date.now(),
        progress: {
          ...job.progress,
          current_file: undefined
        }
      }
      set({ batchPreprocessingJob: failedJob })
    }
  },
  
  startProcessing: async () => {
    const { selectedChunks, llmConfig, preprocessingResult, uploadedFile, groundTruthWitness } = get()
    const selectedGroundTruth = useGroundTruthStore.getState().selectedGroundTruth
    
    // Implement GT priority logic: file upload > witness selection > saved sessions
    let finalGroundTruthResult = null
    let finalLLMConfig = { ...llmConfig }
    
    if (selectedGroundTruth?.content) {
      // Priority 1: Uploaded ground truth file
      finalGroundTruthResult = selectedGroundTruth.content
      console.log('[StartProcessing] Using uploaded ground truth file:', selectedGroundTruth.name)
    } else if (groundTruthWitness?.segmentationResult) {
      // Priority 2: Selected witness as ground truth
      finalGroundTruthResult = groundTruthWitness.segmentationResult
      finalLLMConfig.ground_truth_mode = true
      console.log('[StartProcessing] Using witness as ground truth:', groundTruthWitness.witnessName)
    }
    
    // Force conversation mode: disable parallel processing for GT mode or conversation learning
    if (finalGroundTruthResult || finalLLMConfig.ground_truth_mode) {
      finalLLMConfig.use_parallel_processing = false
      console.log('[StartProcessing] Forcing sequential processing for ground truth/conversation mode')
    }
    
    // Enhanced debugging to identify the issue
    console.log('[StartProcessing] Debug info:', {
      selectedChunksLength: selectedChunks.length,
      selectedChunks: selectedChunks,
      hasPreprocessingResult: !!preprocessingResult,
      preprocessingResultKeys: preprocessingResult ? Object.keys(preprocessingResult) : null,
      groundTruthMode: finalLLMConfig.ground_truth_mode,
      useParallelProcessing: finalLLMConfig.use_parallel_processing,
      selectedGroundTruthId: selectedGroundTruth?.id,
      selectedGroundTruthName: selectedGroundTruth?.name,
      groundTruthWitnessName: groundTruthWitness?.witnessName,
      finalGroundTruthSource: finalGroundTruthResult ? (selectedGroundTruth?.content ? 'file' : 'witness') : 'none'
    })
    
    // DETAILED GROUND TRUTH DEBUGGING
    console.log('[GT-DEBUG] Ground Truth State Analysis:', {
      'selectedGroundTruth exists': !!selectedGroundTruth,
      'selectedGroundTruth?.content exists': !!selectedGroundTruth?.content,
      'groundTruthWitness exists': !!groundTruthWitness,
      'groundTruthWitness?.segmentationResult exists': !!groundTruthWitness?.segmentationResult,
      'finalGroundTruthResult will be sent': !!finalGroundTruthResult,
      'finalGroundTruthResult type': finalGroundTruthResult ? typeof finalGroundTruthResult : 'null',
      'finalGroundTruthResult preview': finalGroundTruthResult ? {
        hasChunkResults: !!finalGroundTruthResult.chunk_results,
        chunkResultsLength: finalGroundTruthResult.chunk_results?.length || 0,
        hasAggregatedStats: !!finalGroundTruthResult.aggregated_statistics
      } : null
    })
    
    if (!selectedChunks.length || !preprocessingResult) {
      console.error('[StartProcessing] EARLY RETURN - Missing required data:', {
        missingChunks: !selectedChunks.length,
        missingPreprocessingResult: !preprocessingResult,
        selectedChunksLength: selectedChunks.length,
        hasPreprocessingResult: !!preprocessingResult
      })
      return
    }
    
    const sessionId = Date.now().toString()
    const session: ProcessingSession = {
      session_id: sessionId,
      status: 'running',
      progress: {
        total_chunks: selectedChunks.length,
        completed_chunks: 0,
        failed_chunks: 0
      },
      start_time: Date.now()
    }
    
    set({ 
      processingSession: session,
      currentStep: 'processing',
      chunkResults: new Map()
    })

    // Set up interruption detection and resume session creation
    const { setupInterruptionDetection } = get()
    const cleanupInterruption = setupInterruptionDetection(sessionId, {
      selectedChunks,
      llmConfig,
      preprocessingResult,
      uploadedFile
    })
    
    // WebSocket connection is now managed by useProcessingWebSocket hook in components
    // This ensures proper cleanup and unified connection management
    
    // Start background processing
    try {
      const requestPayload = {
        session_id: sessionId,
        chunks: selectedChunks,
        llm_config: finalLLMConfig,
        preprocessing_result: preprocessingResult,
        ground_truth_result: finalGroundTruthResult
      }
      
      // DEBUG: Log the exact payload being sent to backend
      console.log('[GT-DEBUG] API Request Payload:', {
        session_id: requestPayload.session_id,
        chunks_count: requestPayload.chunks.length,
        llm_config: requestPayload.llm_config,
        has_preprocessing_result: !!requestPayload.preprocessing_result,
        has_ground_truth_result: !!requestPayload.ground_truth_result,
        ground_truth_result_preview: requestPayload.ground_truth_result ? {
          type: typeof requestPayload.ground_truth_result,
          keys: Object.keys(requestPayload.ground_truth_result),
          chunk_results_length: requestPayload.ground_truth_result.chunk_results?.length || 0
        } : null
      })
      
      const response = await fetch(API_ENDPOINTS.processingStart, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload)
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Failed to start processing')
      }
    } catch (error) {
      console.error('Failed to start processing:', error)
      set({
        processingSession: { ...session, status: 'error' }
      })
      
      // Handle API errors as interruptions
      get().handleProcessingInterruption('error')
      cleanupInterruption()
    }
  },

  startBatchProcessing: async () => {
    const { batchPreprocessingJob, batchSelectedChunks, llmConfig } = get()
    const selectedGroundTruth = useGroundTruthStore.getState().selectedGroundTruth
    
    console.log('startBatchProcessing called:', {
      hasBatchPreprocessingJob: !!batchPreprocessingJob,
      preprocessingJobStatus: batchPreprocessingJob?.status,
      resultsSize: batchPreprocessingJob?.results ? Object.keys(batchPreprocessingJob.results).length : 0,
      selectedChunksSize: Object.keys(batchSelectedChunks).length,
      selectedChunksEntries: Object.entries(batchSelectedChunks)
    })
    
    // Validation checks
    if (!batchPreprocessingJob) {
      console.error('Cannot start batch processing: no preprocessing job found')
      return
    }
    
    if (batchPreprocessingJob.status !== 'completed') {
      console.error('Cannot start batch processing: preprocessing not completed')
      return
    }
    
    if (!batchPreprocessingJob.results || Object.keys(batchPreprocessingJob.results).length === 0) {
      console.error('Cannot start batch processing: no preprocessing results')
      return
    }
    
    if (Object.keys(batchSelectedChunks).length === 0) {
      console.error('Cannot start batch processing: no chunks selected')
      return
    }
    
    const jobId = `batch_seg_${Date.now()}`
    
    // Calculate total chunks across all files
    let totalChunks = 0
    const preprocessingResults: PreprocessingResult[] = []
    
    Object.entries(batchPreprocessingJob.results).forEach(([fileId, result]) => {
      const selectedChunks = batchSelectedChunks[fileId] || []
      totalChunks += selectedChunks.length
      preprocessingResults.push(result)
    })
    
    const job: BatchSegmentationJob = {
      job_id: jobId,
      preprocessing_results: preprocessingResults,
      selected_chunks_per_file: batchSelectedChunks,
      llm_config: llmConfig,
      status: 'pending',
      progress: {
        total_chunks: totalChunks,
        completed_chunks: 0,
        failed_chunks: 0
      },
      results: {},
      start_time: Date.now()
    }
    
    set({ 
      batchSegmentationJob: job,
      currentStep: 'batch-processing'
    })
    
    // First check if backend is responding
    try {
      console.log('Testing backend health before starting batch processing...')
      const healthResponse = await fetch(API_ENDPOINTS.health)
      if (!healthResponse.ok) {
        throw new Error(`Backend health check failed: ${healthResponse.status}`)
      }
      const healthData = await healthResponse.json()
      console.log('Backend health check passed:', healthData)
    } catch (error) {
      console.error('Backend health check failed:', error)
      throw new Error('Backend server is not responding. Please start the server.')
    }
    
    // Start batch processing API call first
    try {
      console.log('Starting batch processing API call...', {
        jobId,
        totalChunks,
        filesCount: preprocessingResults.length,
        selectedChunksCount: batchSelectedChunks
      })
      
      const response = await fetch(API_ENDPOINTS.batchProcessingStart, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: jobId,
          preprocessing_results: preprocessingResults,
          selected_chunks_per_file: batchSelectedChunks,
          llm_config: llmConfig,
          ground_truth_result: selectedGroundTruth?.content || null
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        console.error('Batch processing API error:', errorData)
        throw new Error(errorData.detail || 'Failed to start batch processing')
      }
      
      const result = await response.json()
      console.log('Batch processing started successfully:', result)
      
      // Update status to processing after successful API call
      set({
        batchSegmentationJob: {
          ...job,
          status: 'processing'
        }
      })
      
    } catch (error) {
      console.error('Failed to start batch processing API call:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      
      set({
        batchSegmentationJob: { 
          ...job, 
          status: 'error',
          error_message: errorMessage,
          completion_time: Date.now()
        }
      })
      return // Exit early if API call failed
    }
    
    // WebSocket connection is now managed by useBatchWebSocket hook in components
    // This ensures proper cleanup and unified connection management with advanced features:
    // - Automatic reconnection
    // - Connection pooling
    // - Centralized error handling
    // - Memory leak prevention
  },
  
  pauseProcessing: async () => {
    const session = get().processingSession
    if (!session) return
    
    try {
      await fetch(API_ENDPOINTS.processingPause(session.session_id), { method: 'POST' })
      set({
        processingSession: { ...session, status: 'paused' }
      })
    } catch (error) {
      console.error('Failed to pause processing:', error)
    }
  },
  
  resumeProcessing: async () => {
    const session = get().processingSession
    if (!session) return
    
    try {
      await fetch(API_ENDPOINTS.processingResume(session.session_id), { method: 'POST' })
      set({
        processingSession: { ...session, status: 'running' }
      })
    } catch (error) {
      console.error('Failed to resume processing:', error)
    }
  },
  
  stopProcessing: async () => {
    const session = get().processingSession
    if (!session) return
    
    try {
      await fetch(API_ENDPOINTS.processingStop(session.session_id), { method: 'POST' })
      set({
        processingSession: { ...session, status: 'completed' }
      })
    } catch (error) {
      console.error('Failed to stop processing:', error)
    }
  },

  // Batch processing control functions
  pauseBatchProcessing: async () => {
    const batchJob = get().batchSegmentationJob
    if (!batchJob || !batchJob.job_id) return
    
    try {
      const response = await fetch(`${API_ENDPOINTS.batchProcessingStart.replace('/start', '')}/pause/${batchJob.job_id}`, { 
        method: 'POST' 
      })
      
      if (!response.ok) {
        throw new Error(`Failed to pause batch processing: ${response.status} ${response.statusText}`)
      }
      
      const result = await response.json()
      set({
        batchSegmentationJob: { ...batchJob, status: 'paused' }
      })
      
      console.log('Batch processing paused successfully:', result)
    } catch (error) {
      console.error('Failed to pause batch processing:', error)
      throw error
    }
  },
  
  resumeBatchProcessing: async () => {
    const batchJob = get().batchSegmentationJob
    if (!batchJob || !batchJob.job_id) return
    
    try {
      const response = await fetch(`${API_ENDPOINTS.batchProcessingStart.replace('/start', '')}/resume/${batchJob.job_id}`, { 
        method: 'POST' 
      })
      
      if (!response.ok) {
        throw new Error(`Failed to resume batch processing: ${response.status} ${response.statusText}`)
      }
      
      const result = await response.json()
      set({
        batchSegmentationJob: { ...batchJob, status: 'processing' }
      })
      
      console.log('Batch processing resumed successfully:', result)
    } catch (error) {
      console.error('Failed to resume batch processing:', error)
      throw error
    }
  },
  
  stopBatchProcessing: async () => {
    const batchJob = get().batchSegmentationJob
    if (!batchJob || !batchJob.job_id) return
    
    try {
      const response = await fetch(`${API_ENDPOINTS.batchProcessingStart.replace('/start', '')}/stop/${batchJob.job_id}`, { 
        method: 'POST' 
      })
      
      if (!response.ok) {
        throw new Error(`Failed to stop batch processing: ${response.status} ${response.statusText}`)
      }
      
      const result = await response.json()
      set({
        batchSegmentationJob: { ...batchJob, status: 'stopped' }
      })
      
      console.log('Batch processing stopped successfully:', result)
    } catch (error) {
      console.error('Failed to stop batch processing:', error)
      throw error
    }
  },
  
  resetWorkflow: () => {
    // WebSocket cleanup is now handled by the websocketStore
    // Components using WebSocket hooks will automatically clean up connections
    
    set({
      currentStep: 'upload',
      workflowMode: 'single',
      
      // Reset single file workflow
      uploadedFile: null,
      preprocessingResult: null,
      preprocessingError: null,
      selectedChunks: [],
      
      // Reset batch workflow
      batchFiles: [],
      batchPreprocessingJob: null,
      batchSegmentationJob: null,
      batchSelectedChunks: {},
      
      processingSession: null,
      chunkResults: new Map(),
      selectedChunkForInspection: null,
      
      // Reset render counter
      batchRenderCounter: 0
    })
  },






    createResumeSessionFromCurrentState: async (witnessId: string, chapterId: string, bookId: string, interruption_reason: string): Promise<WitnessResumeSession> => {
    const state = get()
    
    if (!state.uploadedFile || !state.preprocessingResult || !state.processingSession) {
      throw new Error('Insufficient current state to create resume session')
    }

    try {
      const persistentId = `${witnessId}_${Date.now()}`
      const now = Date.now()
      
      // Calculate file hash for integrity checking
      const fileHash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(state.uploadedFile.content))
      const hashArray = Array.from(new Uint8Array(fileHash))
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

      // Calculate preprocessing hash
      const preprocessingHash = JSON.stringify({
        primary_chunks: state.preprocessingResult.primary_chunking?.chunks?.length || 0,
        secondary_chunks: state.preprocessingResult.secondary_chunking?.chunks || 0,
      })

      // Create chunk states from current processing progress
      const chunkStates: Record<string, any> = {}
      const completedResults: Record<string, ChunkProcessingResult> = {}
      const pendingChunks: string[] = []
      const failedChunkIds: string[] = []

      state.selectedChunks.forEach(chunkId => {
        const result = state.chunkResults.get(chunkId)
        if (result) {
          if (result.processing_status === 'success') {
            chunkStates[chunkId] = {
              chunk_id: chunkId,
              status: 'completed',
              attempts: 1,
              last_attempt: now,
              processing_time_ms: result.processing_time,
              can_retry: false,
              estimated_tokens: 100 // Estimate
            }
            completedResults[chunkId] = result
          } else if (result.processing_status === 'error') {
            chunkStates[chunkId] = {
              chunk_id: chunkId,
              status: 'failed',
              attempts: 1,
              last_attempt: now,
              error_message: result.error_message,
              can_retry: true,
              estimated_tokens: 100
            }
            failedChunkIds.push(chunkId)
          }
        } else {
          chunkStates[chunkId] = {
            chunk_id: chunkId,
            status: 'pending',
            attempts: 0,
            last_attempt: 0,
            can_retry: true,
            estimated_tokens: 100
          }
          pendingChunks.push(chunkId)
        }
      })

      const resumeSession: WitnessResumeSession = {
        persistent_id: persistentId,
        witness_id: witnessId,
        chapter_id: chapterId,
        book_id: bookId,
        created_at: now,
        last_activity: now,
        expires_at: now + (30 * 24 * 60 * 60 * 1000), // 30 days
        interruption_reason: interruption_reason as any,
        completion_percentage: Math.round((Object.keys(completedResults).length / state.selectedChunks.length) * 100),
        total_chunks: state.selectedChunks.length,
        completed_chunks: Object.keys(completedResults).length,
        failed_chunks: failedChunkIds.length,
        pending_chunks: pendingChunks,
        failed_chunk_ids: failedChunkIds,
        original_config: {
          llm_config: state.llmConfig,
          trigger_config: state.triggerConfig,
          selected_chunks: state.selectedChunks
        },
        chunk_states: chunkStates,
        completed_results: completedResults,
        file_metadata: {
          filename: state.uploadedFile.name,
          file_hash: hashHex,
          preprocessing_hash: preprocessingHash,
          file_size: state.uploadedFile.size,
          preprocessing_result: state.preprocessingResult
        },
        can_resume: true,
        validation_status: 'pending',
        validation_issues: [],
        processing_metrics: {
          estimated_time_remaining: pendingChunks.length * 30000, // 30s per chunk estimate
          average_chunk_time: 30000,
          total_api_calls: Object.keys(completedResults).length,
          total_cost_usd: Object.keys(completedResults).length * 0.01 // Estimate
        }
      }

      // Save to resumeStore instead
      const { useResumeStore } = await import('./resumeStore')
      await useResumeStore.getState().saveResumeSession(resumeSession)
      return resumeSession
      
    } catch (error) {
      console.error('Failed to create resume session from current state:', error)
      throw error
    }
  },

  createBatchResumeSessionFromCurrentState: async (batchId: string, chapterId: string, bookId: string, interruption_reason: string): Promise<BatchResumeSession> => {
    const state = get()
    
    if (!state.batchSegmentationJob) {
      throw new Error('No batch segmentation job to create resume session from')
    }

    try {
      const persistentId = `batch_${batchId}_${Date.now()}`
      const now = Date.now()
      
      // Create file sessions for each file in the batch
      const fileSessions: Record<string, WitnessResumeSession> = {}
      const filesToResume: string[] = []

      // This would need to be implemented based on batch processing state
      // For now, creating a basic structure
      
      const batchResumeSession: BatchResumeSession = {
        persistent_id: persistentId,
        batch_id: batchId,
        chapter_id: chapterId,
        book_id: bookId,
        created_at: now,
        last_activity: now,
        expires_at: now + (30 * 24 * 60 * 60 * 1000),
        interruption_reason: interruption_reason,
        total_files: state.batchSegmentationJob.preprocessing_results.length,
        completed_files: 0,
        failed_files: 0,
        current_file_index: 0,
        file_sessions: fileSessions,
        original_batch_config: {
          llm_config: state.batchSegmentationJob.llm_config,
          selected_chunks_per_file: state.batchSegmentationJob.selected_chunks_per_file,
          trigger_config: state.batchTriggerConfig
        },
        can_resume: true,
        files_to_resume: filesToResume,
        estimated_time_remaining: 0,
        batch_metrics: {
          total_chunks_across_files: state.batchSegmentationJob.progress.total_chunks,
          completed_chunks_across_files: state.batchSegmentationJob.progress.completed_chunks,
          total_cost_usd: 0,
          average_file_processing_time: 0
        }
      }

      // Save to resumeStore instead
      const { useResumeStore } = await import('./resumeStore')
      await useResumeStore.getState().saveBatchResumeSession(batchResumeSession)
      return batchResumeSession
      
    } catch (error) {
      console.error('Failed to create batch resume session from current state:', error)
      throw error
    }
  },

  // Resume state management
  updateResumeSessionProgress: async (chunkId: string, result: ChunkProcessingResult): Promise<void> => {
    const state = get()
    if (!state.currentResumeSession) return

    try {
      const session = { ...state.currentResumeSession }
      
      // Update chunk state
      session.chunk_states[chunkId] = {
        chunk_id: chunkId,
        status: result.processing_status === 'success' ? 'completed' : 'failed',
        attempts: (session.chunk_states[chunkId]?.attempts || 0) + 1,
        last_attempt: Date.now(),
        processing_time_ms: result.processing_time,
        error_message: result.error_message,
        can_retry: result.processing_status === 'error',
        estimated_tokens: 100 // Estimate
      }

      // Update completed results if successful
      if (result.processing_status === 'success') {
        session.completed_results[chunkId] = result
        session.completed_chunks = Object.keys(session.completed_results).length
        
        // Remove from pending and failed chunks
        session.pending_chunks = session.pending_chunks.filter(id => id !== chunkId)
        session.failed_chunk_ids = session.failed_chunk_ids.filter(id => id !== chunkId)
      } else if (result.processing_status === 'error') {
        session.failed_chunks++
        if (!session.failed_chunk_ids.includes(chunkId)) {
          session.failed_chunk_ids.push(chunkId)
        }
        session.pending_chunks = session.pending_chunks.filter(id => id !== chunkId)
      }

      // Update completion percentage
      session.completion_percentage = Math.round((session.completed_chunks / session.total_chunks) * 100)
      session.last_activity = Date.now()

      // Save to resumeStore instead\n      const { useResumeStore } = await import('./resumeStore')\n      await useResumeStore.getState().saveResumeSession(session)
    } catch (error) {
      console.error('Failed to update resume session progress:', error)
    }
  },

  setupInterruptionDetection: (sessionId: string, context: {
    selectedChunks: string[],
    llmConfig: LLMConfig,
    preprocessingResult: PreprocessingResult,
    uploadedFile: TextFile | null
  }) => {
    const state = get()
    
    // Store context for interruption handling
    const interruptionContext = { sessionId, ...context }
    
    console.log('Setting up interruption detection for session:', sessionId)
    
    // Browser close/refresh detection
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      const currentSession = get().processingSession
      if (currentSession && currentSession.status === 'running') {
        console.log('Browser closing during active processing, creating resume session')
        
        // Create resume session synchronously (must be sync for beforeunload)
        get().createResumeSessionFromCurrentState('unknown_witness', 'unknown_chapter', 'unknown_book', 'browser_crash')
          .catch(error => console.error('Failed to create resume session on browser close:', error))
        
        // Show confirmation dialog
        event.preventDefault()
        event.returnValue = 'Processing is in progress. Are you sure you want to leave?'
        return event.returnValue
      }
    }
    
    // Page visibility change detection (tab switching, minimizing)
    const visibilityChangeHandler = () => {
      if (document.hidden) {
        const currentSession = get().processingSession
        if (currentSession && currentSession.status === 'running') {
          console.log('Page hidden during processing, monitoring for potential interruption')
          // Don't create resume session immediately, just log
        }
      }
    }
    
    // Network status change detection
    const onlineHandler = () => {
      console.log('Network connection restored')
    }
    
    const offlineHandler = () => {
      const currentSession = get().processingSession
      if (currentSession && currentSession.status === 'running') {
        console.log('Network connection lost during processing')
        get().handleProcessingInterruption('network_failure')
      }
    }
    
    // Add event listeners
    window.addEventListener('beforeunload', beforeUnloadHandler)
    document.addEventListener('visibilitychange', visibilityChangeHandler)
    window.addEventListener('online', onlineHandler)
    window.addEventListener('offline', offlineHandler)
    
    // Store cleanup functions
    const cleanup = () => {
      console.log('Cleaning up interruption detection for session:', sessionId)
      window.removeEventListener('beforeunload', beforeUnloadHandler)
      document.removeEventListener('visibilitychange', visibilityChangeHandler)
      window.removeEventListener('online', onlineHandler)
      window.removeEventListener('offline', offlineHandler)
      
      // Remove from interruption listeners array
      set(state => ({
        interruptionListeners: state.interruptionListeners.filter(listener => listener !== cleanup)
      }))
    }
    
    // Add to interruption listeners for later cleanup
    set(state => ({
      interruptionListeners: [...state.interruptionListeners, cleanup]
    }))
    
    return cleanup
  },

  markResumeSessionComplete: async (): Promise<void> => {
    const state = get()
    if (!state.currentResumeSession) return

    try {
      const session = { ...state.currentResumeSession }
      session.completion_percentage = 100
      session.can_resume = false
      session.last_activity = Date.now()
      session.pending_chunks = []

      // Save to resumeStore instead\n      const { useResumeStore } = await import('./resumeStore')\n      await useResumeStore.getState().saveResumeSession(session)
      
      // Clear current resume session
      set({ currentResumeSession: null })
    } catch (error) {
      console.error('Failed to mark resume session complete:', error)
    }
  },

  handleProcessingInterruption: async (reason: string): Promise<void> => {
    const state = get()
    
    // Only create resume session if we have a current processing session
    if (!state.processingSession || !state.uploadedFile) return

    try {
      console.log('Processing interrupted:', reason)
      
      // Create resume session from current state
      // For now, use placeholder values - in production this would need proper context
      const resumeSession = await get().createResumeSessionFromCurrentState(
        'current_witness_id', // This would need to be passed from the UI context
        'current_chapter_id', // This would need to be passed from the UI context  
        'current_book_id',    // This would need to be passed from the UI context
        reason
      )
      
      console.log('Resume session created for interruption:', resumeSession.persistent_id)
      
      // Update processing session status
      if (state.processingSession) {
        set({
          processingSession: {
            ...state.processingSession,
            status: 'error' // Mark as interrupted
          }
        })
      }
      
    } catch (error) {
      console.error('Failed to handle processing interruption:', error)
    }
  },

  // Witness preprocessing implementations
  startWitnessPreprocessing: async (witnessFile: TextFile, config: TriggerConfig): Promise<PreprocessingResult> => {
    try {
      // API call to preprocessing endpoint
      const response = await fetch(API_ENDPOINTS.preprocessing, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: witnessFile.content,
          config: config
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Witness preprocessing failed')
      }
      
      const result = await response.json()
      return { ...result, file_id: witnessFile.id }
    } catch (error) {
      console.error('Witness preprocessing failed:', error)
      throw error
    }
  },

  preprocessWitnessWithConfig: async (witnessId: string, witnessContent: string, config: TriggerConfig): Promise<PreprocessingResult> => {
    try {
      // API call to preprocessing endpoint
      const response = await fetch(API_ENDPOINTS.preprocessing, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: witnessContent,
          config: config
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Witness preprocessing failed')
      }
      
      const result = await response.json()
      return { ...result, file_id: witnessId }
    } catch (error) {
      console.error('Witness preprocessing with config failed:', error)
      throw error
    }
  },

  removeInterruptionDetection: () => {
    const { interruptionListeners } = get()
    interruptionListeners.forEach(cleanup => {
      try {
        cleanup()
      } catch (error) {
        console.error('Error removing interruption listener:', error)
      }
    })
    set({ interruptionListeners: [] })
  },

  addInterruptionListener: (listener: () => void) => {
    set(state => ({
      interruptionListeners: [...state.interruptionListeners, listener]
    }))
  },

  removeInterruptionListener: (listener: () => void) => {
    set(state => ({
      interruptionListeners: state.interruptionListeners.filter(l => l !== listener)
    }))
  },

  // Ground truth management functions
  setGroundTruthWitness: (groundTruth: GroundTruthData | null) => {
    console.log('🔍 SET GROUND TRUTH: Called with data:', {
      hasData: !!groundTruth,
      witnessName: groundTruth?.witnessName,
      totalUnits: groundTruth?.totalUnits,
      setTimestamp: groundTruth?.setTimestamp
    })
    
    set({ groundTruthWitness: groundTruth })
    const { _persistGroundTruthWitness } = get()
    _persistGroundTruthWitness(groundTruth)
    
    // Verify it was set in the store
    const currentState = get().groundTruthWitness
    console.log('🔍 SET GROUND TRUTH: Verified in store:', {
      storeHasGroundTruth: !!currentState,
      storeWitnessName: currentState?.witnessName
    })
    
    console.log('Ground truth witness set:', groundTruth ? groundTruth.witnessName : 'cleared')
  },

  clearGroundTruth: () => {
    set({ groundTruthWitness: null })
    const { _persistGroundTruthWitness } = get()
    _persistGroundTruthWitness(null)
    console.log('Ground truth cleared')
  },

  autoApplyWithGroundTruth: async (sessionId: string, witnessId?: string): Promise<void> => {
    const { groundTruthWitness } = get()
    
    if (!groundTruthWitness) {
      throw new Error('No ground truth witness set. Please set a ground truth first.')
    }

    try {
      console.log('🎯 FRONTEND: Starting AI refinement with ground truth:', groundTruthWitness.witnessName)
      console.log('🎯 FRONTEND: Ground truth data structure:', {
        witnessName: groundTruthWitness.witnessName,
        totalUnits: groundTruthWitness.totalUnits,
        totalChunks: groundTruthWitness.totalChunks,
        setTimestamp: groundTruthWitness.setTimestamp,
        hasSegmentationResult: !!groundTruthWitness.segmentationResult,
        chunkResultsCount: groundTruthWitness.segmentationResult?.chunk_results?.length || 0
      })
      
      // Log first few units for verification
      const firstChunk = groundTruthWitness.segmentationResult?.chunk_results?.[0]
      if (firstChunk?.micro_units) {
        console.log('🎯 FRONTEND: First GT chunk units:')
        firstChunk.micro_units.slice(0, 3).forEach((unit: any, i: number) => {
          console.log(`  GT Unit ${i}: "${unit.unit_text_content?.slice(0, 50)}${unit.unit_text_content?.length > 50 ? '...' : ''}"`)
        })
      }
      
      const response = await fetch(API_ENDPOINTS.editSessionAiRefineWithGroundTruth(sessionId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: 'frontend_user',
          witness_id: witnessId,
          ground_truth_data: groundTruthWitness
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || `Ground truth refinement failed: ${response.statusText}`)
      }

      const result = await response.json()
      console.log('Ground truth AI refinement completed:', result)
      
      return result
    } catch (error) {
      console.error('Ground truth AI refinement failed:', error)
      throw error
    }
  },

  // Refresh processing results after sync events
  refreshProcessingResults: async () => {
    const state = get()
    if (!state.processingSession) return
    
    try {
      console.log('[SegmentationStore] Refreshing processing results for session:', state.processingSession.session_id)
      
      // Fetch latest session status
      const response = await fetch(API_ENDPOINTS.processingStatus(state.processingSession.session_id))
      if (response.ok) {
        const sessionData = await response.json()
        set({ processingSession: sessionData })
      }
    } catch (error) {
      console.error('Failed to refresh processing results:', error)
    }
  },

    // Note: Resume, session management, and unit editing functions are available in their respective stores
  }
})